import React, { useState, useEffect, useReducer, useContext } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Search as SearchIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../services/api";
import SendSmsModal from "../../components/SendSmsModal";
import useSmsEnabled from "../../hooks/useSmsEnabled";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  searchBar: {
    marginBottom: theme.spacing(3),
  },
  statusChip: {
    fontWeight: 600,
  },
  tableContainer: {
    maxHeight: "calc(100vh - 300px)",
  },
  noData: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_SMS":
      return { ...state, smsMessages: action.payload, loading: false };
    case "UPDATE_SMS":
      const updatedMessages = state.smsMessages.map((sms) =>
        sms.id === action.payload.id ? action.payload : sms
      );
      return { ...state, smsMessages: updatedMessages };
    case "DELETE_SMS":
      const filteredMessages = state.smsMessages.filter(
        (sms) => sms.id !== action.payload
      );
      return { ...state, smsMessages: filteredMessages };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

const SmsPage = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [state, dispatch] = useReducer(reducer, {
    smsMessages: [],
    loading: true,
  });

  const [searchParam, setSearchParam] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSms, setSelectedSms] = useState(null);
  const { enabled, balance } = useSmsEnabled();

  useEffect(() => {
    if (enabled) {
      loadSms();
      loadUsers();
    }
  }, [enabled]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.users || []);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  };

  const loadSms = async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      
      const params = {
        searchParam,
        pageNumber: 1,
      };

      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (filterUserId) params.filterUserId = filterUserId;

      const { data } = await api.get("/sms", { params });

      dispatch({ type: "LOAD_SMS", payload: data.smsMessages });
    } catch (error) {
      console.error("Erro ao carregar SMS:", error);
      toast.error("Erro ao carregar histórico de SMS");
    }
  };

  const handleDeleteSms = async (smsId) => {
    if (!window.confirm("Deseja realmente excluir este SMS do histórico?")) {
      return;
    }

    try {
      await api.delete(`/sms/${smsId}`);
      dispatch({ type: "DELETE_SMS", payload: smsId });
      toast.success("SMS excluído do histórico");
    } catch (error) {
      console.error("Erro ao excluir SMS:", error);
      toast.error("Erro ao excluir SMS");
    }
    handleCloseMenu();
  };

  const handleMenuClick = (event, sms) => {
    setAnchorEl(event.currentTarget);
    setSelectedSms(sms);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedSms(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "default",
      sent: "primary",
      delivered: "primary",
      failed: "secondary",
    };
    return colors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Pendente",
      sent: "Enviado",
      delivered: "Entregue",
      failed: "Falhou",
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return format(parseISO(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return "—";
    }
  };

  if (!enabled) {
    return (
      <Paper className={classes.root}>
        <Box className={classes.noData}>
          <Typography variant="h6" gutterBottom>
            Módulo SMS não configurado
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Acesse Configurações &gt; SMS para configurar a integração
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h4">SMS</Typography>
          <Typography variant="body2" color="textSecondary">
            Saldo: {balance !== null ? `${balance} créditos` : "—"}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.searchBar}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              placeholder="Buscar por telefone..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && loadSms()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Inicial"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Data Final"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Filtrar por Usuário</InputLabel>
              <Select
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
                label="Filtrar por Usuário"
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={1}>
            <IconButton
              onClick={loadSms}
              style={{ marginTop: 4 }}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      <TableContainer className={classes.tableContainer}>
        {state.loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : state.smsMessages.length === 0 ? (
          <Box className={classes.noData}>
            <Typography variant="body1">Nenhum SMS encontrado</Typography>
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Contato</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Mensagem</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data/Hora</TableCell>
                <TableCell>Custo</TableCell>
                {user?.profile === "admin" && (
                  <TableCell align="right">Ações</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {state.smsMessages.map((sms) => (
                <TableRow key={sms.id}>
                  <TableCell>{sms.contact?.name || "—"}</TableCell>
                  <TableCell>{sms.phone}</TableCell>
                  <TableCell>
                    <Tooltip title={sms.message}>
                      <span>
                        {sms.message.length > 50
                          ? `${sms.message.substring(0, 50)}...`
                          : sms.message}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{sms.user?.name || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(sms.status)}
                      color={getStatusColor(sms.status)}
                      size="small"
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell>{formatDate(sms.sentAt || sms.createdAt)}</TableCell>
                  <TableCell>{sms.cost ? `${sms.cost} crédito(s)` : "—"}</TableCell>
                  {user?.profile === "admin" && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, sms)}
                      >
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handleDeleteSms(selectedSms?.id)}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Excluir
        </MenuItem>
      </Menu>

      <SendSmsModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        contact={{ number: "", name: "" }}
        onSuccess={loadSms}
      />
    </Paper>
  );
};

export default SmsPage;
